import classNames from 'classnames';
import styles from './quiz-multi-select-field.module.scss';
import { useCallback, useContext, useEffect, useState } from 'react';
import { QuizContext } from 'src/views/Quiz/Quiz.context';
import { QUIZ_EVENT_TYPES } from 'src/types/Quiz';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/pro-light-svg-icons';
import { faInfoCircle as faInfoCircleSolid } from '@fortawesome/pro-solid-svg-icons';
import MoshModal from 'src/components/MoshModal';
import { trackClick } from 'src/analytics/Events';
import EventNames from 'src/analytics/EventNames';
import { Vertical } from 'src/constants/Verticals';
import { env, isMoshy } from 'src/env.mjs';
export interface ItemProps {
    answerHeading?: string;
    answerDescription?: string;
}

const QuizMultiSelectField = () => {
    const {
        quizName,
        quizVersion,
        quizType,
        currentQuestion,
        handleSubmitAnswer,
        prefillQuestionIds,
        submittedQuestionIds,
        inQueue,
        state,
    } = useContext(QuizContext);
    const {
        answerList,
        questionId,
        questionText,
        questionExplanation,
        additionalField1: modalHeading,
        additionalField2: modalDescription,
        userAnswers,
        prefillAnswers,
        prefillNeeded,
        medication,
    } = currentQuestion || {};
    const [pageTransitioning] = state.pageTransitioning;
    const [continueDisabled, setContinueDisabled] = state.continueDisabled;
    const [loading] = state.loading;
    const [selectedIndexes, setSelectedIndexes] = useState<number[]>([]);
    const [showModal, setShowModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState<ItemProps | null>(null);

    const handleToggleSelected = useCallback(
        (currentIndex: number) => {
            const { deselectOthers } = answerList[currentIndex];
            let newSelectedIndexes = Array.from(selectedIndexes).filter(
                (i) => !answerList[i].deselectOthers,
            );
            if (newSelectedIndexes.includes(currentIndex)) {
                newSelectedIndexes = newSelectedIndexes.filter(
                    (i) => i !== currentIndex,
                );
            } else {
                if (deselectOthers) {
                    newSelectedIndexes = [currentIndex];
                } else {
                    newSelectedIndexes.push(currentIndex);
                }
            }
            setSelectedIndexes(newSelectedIndexes.sort());
        },
        [answerList, selectedIndexes],
    );

    const handleOpenModal = (
        item: ItemProps,
        e?: React.MouseEvent<HTMLElement>,
    ) => {
        if (e) e.stopPropagation();
        setSelectedItem(item);
        setShowModal(true);
        trackClick(EventNames.QUIZ_BUTTON_CLICKED, {
            quiz_name: `${quizName} - ${quizVersion}`,
            vertical: Vertical[quizType.toUpperCase()],
            label: 'Why are we asking for this',
            question_name: questionText,
        });
    };

    // Set existing answer
    useEffect(() => {
        let selectedAnswerIds: string[] | null = null;
        if (
            questionId &&
            submittedQuestionIds.includes(questionId) &&
            userAnswers.length > 0
        ) {
            selectedAnswerIds = userAnswers.map((a) => a.answerId);
        } else if (
            inQueue &&
            prefillNeeded &&
            prefillQuestionIds.includes(questionId) &&
            prefillAnswers.length > 0
        ) {
            selectedAnswerIds = prefillAnswers.map((a) => a.answerId);
        }
        if (Array.isArray(selectedAnswerIds)) {
            const newSelectedIndexes = selectedAnswerIds.reduce(
                (acc, selectedAnswerId) => [
                    ...acc,
                    answerList.findIndex(
                        (a) => a.answerId === selectedAnswerId,
                    ),
                ],
                [] as number[],
            );
            setSelectedIndexes(newSelectedIndexes);
        } else {
            setSelectedIndexes([]);
        }
    }, [
        questionId,
        userAnswers,
        prefillAnswers,
        prefillNeeded,
        prefillQuestionIds,
        submittedQuestionIds,
        inQueue,
        answerList,
    ]);
    // Handle submitting
    const handleSubmit = useCallback(() => {
        if (selectedIndexes.length > 0 && !continueDisabled && questionId) {
            handleSubmitAnswer({
                questionId: questionId,
                questionText: questionText,
                answerIds: selectedIndexes.map((i) => answerList[i].answerId),
                answerTexts: selectedIndexes.map(
                    (i) => answerList[i].answerText,
                ),
                medication: medication,
            });
        }
    }, [
        continueDisabled,
        selectedIndexes,
        answerList,
        questionId,
        questionText,
        medication,
        handleSubmitAnswer,
    ]);
    // Continue click listener
    useEffect(() => {
        window.addEventListener(QUIZ_EVENT_TYPES.continueClick, handleSubmit);

        return () =>
            window.removeEventListener(
                QUIZ_EVENT_TYPES.continueClick,
                handleSubmit,
            );
    }, [handleSubmit]);
    // Enter key press listener
    useEffect(() => {
        const handleKeyPress = (e: KeyboardEvent) => {
            if (e.key === 'Enter') handleSubmit();
            if (parseInt(e.key)) {
                const num = parseInt(e.key);
                if (num <= answerList.length) handleToggleSelected(num - 1);
            }
        };
        window.addEventListener('keydown', handleKeyPress, false);

        return () =>
            window.removeEventListener('keydown', handleKeyPress, false);
    }, [answerList, handleSubmit, handleToggleSelected]);
    // Continue state
    useEffect(() => {
        if (!pageTransitioning) {
            setContinueDisabled(selectedIndexes.length === 0);
        }
    }, [selectedIndexes, pageTransitioning, setContinueDisabled]);

    return (
        <div
            className={classNames(styles['quiz-multi-select--wrapper'], {
                [styles['loading']]: loading,
            })}
        >
            {/* Content header */}
            <div className={classNames(styles['quiz-field-header'])}>
                {/* Title */}
                <h4 className={classNames(styles['title'])}>{questionText}</h4>
                {/* Subtitle */}
                {questionExplanation ? (
                    <h5
                        className={classNames(
                            styles['subtitle'],
                            'highlight pt-2',
                        )}
                    >
                        {questionExplanation}
                    </h5>
                ) : null}
                {modalHeading ? (
                    <button
                        className={classNames(
                            styles['quiz-info--wrapper'],
                            'mt-2 flex items-center gap-x-2',
                            !(modalHeading && modalDescription)
                                ? 'cursor-default'
                                : '',
                        )}
                        onClick={() => {
                            if (modalHeading && modalDescription) {
                                handleOpenModal({
                                    answerHeading: modalHeading,
                                    answerDescription: modalDescription,
                                });
                            }
                        }}
                    >
                        {modalHeading && modalDescription ? (
                            <span className="h-6 w-6">
                                <FontAwesomeIcon
                                    icon={
                                        isMoshy
                                            ? faInfoCircle
                                            : faInfoCircleSolid
                                    }
                                    className="mr-2 !h-full w-full"
                                />
                            </span>
                        ) : null}
                        <h5 className={styles['quiz-info--message']}>
                            {modalHeading}
                        </h5>
                    </button>
                ) : null}
            </div>
            {/* List of answers */}
            <div className={classNames(styles['answers-list'])}>
                {/* Answer */}
                {answerList.map((a, i) => (
                    <div
                        key={a.answerId}
                        data-testid="quiz-answer"
                        data-answer-id={a.answerId}
                        className={classNames(
                            styles['answer-item--wrapper'],
                            selectedIndexes.includes(i)
                                ? styles['item-selected']
                                : '',
                            loading
                                ? 'pointer-events-none'
                                : 'pointer-events-auto',
                        )}
                    >
                        <div
                            className={classNames(
                                'flex',
                                styles['answer-item'],
                                selectedIndexes.includes(i)
                                    ? styles['item-selected']
                                    : '',
                            )}
                            onClick={() => handleToggleSelected(i)}
                        >
                            <span
                                className={classNames(
                                    styles['item-number'],
                                    'p1 highlight',
                                )}
                            >
                                {i + 1}
                            </span>
                            <span
                                className={classNames(
                                    styles['item-text'],
                                    'p1',
                                )}
                            >
                                {a.answerText}
                            </span>
                            {/* Show info popup if additional info provided */}
                            {a.additionalField1 ? (
                                <button
                                    className={classNames(
                                        styles['info-button'],
                                    )}
                                    onClick={(e) =>
                                        handleOpenModal(
                                            {
                                                answerHeading: a.answerText,
                                                answerDescription:
                                                    a.additionalField1,
                                            },
                                            e,
                                        )
                                    }
                                >
                                    <FontAwesomeIcon
                                        icon={
                                            isMoshy
                                                ? faInfoCircle
                                                : faInfoCircleSolid
                                        }
                                        className="!h-auto w-6"
                                    />
                                </button>
                            ) : null}
                            {a.image ? (
                                <img
                                    src={a.image}
                                    alt={a.answerText}
                                    className="h-16 w-16"
                                />
                            ) : null}
                        </div>
                    </div>
                ))}
                {showModal && selectedItem && (
                    <MoshModal
                        open={showModal}
                        ariaLabel={selectedItem.answerHeading}
                        onModalClose={(value) => setShowModal(value || false)}
                        custom={true}
                        theme={`theme-${env.NEXT_PUBLIC_BRAND}`}
                    >
                        <div>
                            <h4 className="first-letter:capitalize md:pr-2">
                                {selectedItem.answerHeading}
                            </h4>
                            <p className="mb-0 mt-2">
                                {selectedItem.answerDescription}
                            </p>
                        </div>
                    </MoshModal>
                )}
            </div>
        </div>
    );
};

export default QuizMultiSelectField;
